import { Fragment, type ReactNode } from 'react';

type PreviewMessage = {
  type: string;
  content: any;
};

type Props = {
  messages: PreviewMessage[];
  profileName?: string;
  profilePhoto?: string;
  buttonText?: string;
  official?: boolean;
};

function formatWhatsAppText(text: string): ReactNode[] {
  const parts = String(text || '').split(/(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|https?:\/\/[^\s]+)/g);
  return parts.map((part, index) => {
    if (/^\*[^*]+\*$/.test(part)) return <strong key={index}>{part.slice(1, -1)}</strong>;
    if (/^_[^_]+_$/.test(part)) return <em key={index}>{part.slice(1, -1)}</em>;
    if (/^~[^~]+~$/.test(part)) return <s key={index}>{part.slice(1, -1)}</s>;
    if (/^https?:\/\//.test(part)) return <span key={index} className="text-sky-700 underline break-all">{part}</span>;
    return <Fragment key={index}>{part}</Fragment>;
  });
}

function MessageBubble({ message, buttonText }: { message: PreviewMessage; buttonText?: string }) {
  const content = message.content || {};
  if (message.type === 'wait') {
    return <div className="mx-auto rounded-full bg-white/80 px-3 py-1 text-[10px] text-gray-500 shadow">Espera de {content.waitTime || 0}s</div>;
  }
  if (message.type === 'image' && content.url) {
    return (
      <div className="ml-auto max-w-[88%] rounded-lg bg-[#d9fdd3] p-1.5 shadow-sm">
        <img src={content.url} alt="" className="max-h-44 w-full rounded object-cover" />
        {content.caption && <p className="whitespace-pre-wrap px-1.5 py-1 text-sm">{formatWhatsAppText(content.caption)}</p>}
      </div>
    );
  }
  if (message.type === 'video' || message.type === 'audio' || message.type === 'document' || message.type === 'voice_clone') {
    const labels: Record<string, string> = { video: 'Vídeo', audio: 'Áudio', document: 'Documento', voice_clone: 'Mensagem de voz' };
    return (
      <div className="ml-auto flex max-w-[88%] items-center gap-2 rounded-lg bg-[#d9fdd3] px-3 py-2 text-sm shadow-sm">
        <span className="text-lg">{message.type === 'document' ? '📄' : message.type === 'video' ? '▶️' : '🎙️'}</span>
        <span>{labels[message.type]}</span>
      </div>
    );
  }
  const text = content.text || content.variations?.find(Boolean) || content.user || 'Sua mensagem aparecerá aqui';
  return (
    <div className="ml-auto max-w-[88%] overflow-hidden rounded-lg rounded-tr-none bg-[#d9fdd3] text-sm text-gray-900 shadow-sm">
      <div className="px-3 py-2">
        <p className="whitespace-pre-wrap break-words">{formatWhatsAppText(text)}</p>
        <p className="mt-1 text-right text-[9px] text-gray-500">agora ✓✓</p>
      </div>
      {buttonText && (
        <div className="border-t border-black/10 bg-white/75 px-4 py-2 text-center font-medium text-sky-700">
          ↗ {buttonText}
        </div>
      )}
    </div>
  );
}

export function CampaignMessagePreview({ messages, profileName = 'Sua empresa', profilePhoto, buttonText, official }: Props) {
  const visibleMessages = messages.filter(message => message.type !== 'wait' || Number(message.content?.waitTime) > 0);
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-300 bg-white shadow-sm">
      <div className="flex items-center gap-3 bg-[#075e54] px-3 py-2.5 text-white">
        <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-white/20 text-sm font-bold">
          {profilePhoto ? <img src={profilePhoto} alt="" className="h-full w-full object-cover" /> : profileName.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{profileName || 'Sua empresa'}</p>
          <p className="text-[10px] text-white/75">{official ? 'conta comercial oficial' : 'online'}</p>
        </div>
      </div>
      <div
        className="min-h-64 space-y-2 bg-[#efeae2] p-3"
        style={{ backgroundImage: 'radial-gradient(rgba(0,0,0,.035) 1px, transparent 1px)', backgroundSize: '13px 13px' }}
      >
        {visibleMessages.length > 0
          ? visibleMessages.map((message, index) => <MessageBubble key={index} message={message} buttonText={index === visibleMessages.length - 1 ? buttonText : undefined} />)
          : <MessageBubble message={{ type: 'text', content: { text: '' } }} buttonText={buttonText} />}
      </div>
    </div>
  );
}
